import React, { useEffect, useState, useCallback } from "react";
import { Paper, Typography, Button, TextField, Select, MenuItem, IconButton } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useHasRole } from "../../../hooks/useHasRole";

type User = { _id: string; name: string; email: string; role: string };

const roles = ["ADMIN", "MANAGER", "TECHNICIAN", "CASHIER", "RECEPTIONIST"];

export default function UsersPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const canManage = useHasRole("ADMIN");
  const canView = useHasRole(["ADMIN", "MANAGER"]);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({ name: "", email: "", password: "", role: "TECHNICIAN" });
  const valid = form.name && form.email && form.password;

  const fetchUsers = useCallback(async () => {
    if (!token || !canView) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/users", { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token, canView]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async () => {
    if (!canManage) return;
    try {
      await api.post("/users", form, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ name: "", email: "", password: "", role: "TECHNICIAN" });
      fetchUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to create user");
    }
  };

  const updateRole = async (id: string, role: string) => {
    if (!canManage) return;
    try {
      await api.patch(`/users/${id}`, { role }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((u) => u.map((x) => (x._id === id ? { ...x, role } : x)));
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to update role");
    }
  };

  const removeUser = async (id: string) => {
    if (!canManage) return;
    if (!window.confirm("Remove this user?")) return;
    try {
      await api.delete(`/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((u) => u.filter((x) => x._id !== id));
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to remove user");
    }
  };

  if (!canView) {
    return (
      <Paper elevation={0} className="p-6 rounded-2xl" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <Typography>You do not have permission to view users.</Typography>
      </Paper>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <Typography variant="h5" fontWeight={900} gutterBottom>Users</Typography>
      {error && (
        <Paper elevation={0} className="p-3 mb-4 rounded-xl" sx={{ border: '1px solid', borderColor: 'error.main', bgcolor: 'rgba(239,68,68,0.08)' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {canManage && (
        <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={800} gutterBottom>Add User</Typography>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <TextField label="Name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} size="small" />
            <TextField label="Email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} size="small" />
            <TextField label="Password" type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} size="small" />
            <Select size="small" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value as string})}>
              {roles.map(r=> <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </div>
          <div className="flex justify-end mt-3">
            <Button disabled={!valid} variant="contained" onClick={createUser}>Create</Button>
          </div>
        </Paper>
      )}

      <Paper elevation={0} className="p-0 overflow-x-auto rounded-2xl" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-3 text-left text-gray-700">Name</th>
              <th className="py-2 px-3 text-left text-gray-700">Email</th>
              <th className="py-2 px-3 text-left text-gray-700">Role</th>
              {canManage && <th className="py-2 px-3 text-center text-gray-700">Action</th>}
            </tr>
          </thead>
          <tbody>
            {!users.length && !loading && (
              <tr><td colSpan={4} className="text-center text-gray-400 py-6">No users</td></tr>
            )}
            {users.map((u)=> (
              <tr key={u._id} className="border-b border-gray-200">
                <td className="py-2 px-3">{u.name}</td>
                <td className="py-2 px-3">{u.email}</td>
                <td className="py-2 px-3">
                  {canManage ? (
                    <Select size="small" value={u.role} onChange={(e)=>updateRole(u._id, e.target.value as string)}>
                      {roles.map(r=> <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  ) : (
                    u.role
                  )}
                </td>
                {canManage && (
                  <td className="py-2 px-3 text-center">
                    <IconButton color="error" onClick={()=>removeUser(u._id)} size="small"><DeleteIcon /></IconButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-6">Loading…</div>}
      </Paper>
    </div>
  );
}


